import { FlagsModule } from '@seddi/ui-feature-flags';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { downloadImage } from '../../actions/captureImage';
import { setViewerRef } from '../../store/appRefsSlice';
import { getScene } from '../../store/appSlice';
import type { TRootState } from '../../store/appStateTypes';
import {
  HEADWEAR_CAMERA_ANGLE_OFFSET,
  computeSceneLookAt,
} from '../../viewer/sceneLookAt';

const featureFlagsDef = {
  depthPeelingLayers: 0,
  disableTransparencyAngleBasedCompensation: false,
  disableTransparencyFlatCompensation: false,
  mipMapsGaussianBlur: false,
  parallax: false,
  premultipliedAlphaLogos: true,
  wireframeFabrics: false,
} as const;

const visibleDef = {
  avatar: false,
  background: false,
  garmentHem: true,
  garmentPiece: true,
  garmentSeam: true,
  ground: false,
  logos: true,
} as const;

export const useAppViewer = () => {
  const dispatch = useDispatch();
  const sceneIsLoading = useSelector((state: TRootState) => state.app.sceneIsLoading);
  const sceneVersion = useSelector((state: TRootState) => state.app.sceneVersion);
  const stylePreviewUrl = useSelector((state: TRootState) => state.app.stylePreviewUrl);
  const styleNotFoundMessage = useSelector((state: TRootState) => state.app.styleNotFoundMessage);
  const styleType = useSelector((state: TRootState) => state.app.styleType);

  const viewerRef = useRef<any>(null);
  const lastFramedSceneVersionRef = useRef<number | null>(null);

  useEffect(() => {
    // communicate viewerRef through state
    dispatch(setViewerRef(viewerRef));
    // clear viewerRef state
    return () => {
      dispatch(setViewerRef(null));
    };
  }, [dispatch]);

  const config = useMemo(() => {
    const useStitchesDecals = FlagsModule.isEnabled('use_stitches_decals');
    return { frustumCulling: false, useStitchesDecals };
  }, []);

  const scene = useMemo(() => {
    return getScene();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sceneVersion is intended to memoize the new scene
  }, [sceneVersion]);

  const featureFlags = featureFlagsDef;
  const visible = visibleDef;

  const handleDownloadImage = useCallback(() => {
    if (viewerRef.current) {
      downloadImage(viewerRef.current, 'image/webp');
    }
  }, []);

  // Callback that fires after each render completes
  // This resolves promises from waitForRender() calls
  const handleUpdate = useCallback(() => {}, []);

  const frameScene = useCallback(
    (options?: { force?: boolean }) => {
      const { force = false } = options ?? {};

      if (!scene || sceneIsLoading) {
        return;
      }

      if (!force && lastFramedSceneVersionRef.current === sceneVersion) {
        return;
      }

      const viewer = viewerRef.current;
      if (!viewer) {
        return;
      }

      const canvas = viewer.canvasRef?.current as HTMLCanvasElement | null | undefined;
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        return;
      }

      const aspectRatio = canvas.width / canvas.height;
      const angleY =
        styleType?.toLowerCase() === 'headwear' ? HEADWEAR_CAMERA_ANGLE_OFFSET : 0;
      const camera = computeSceneLookAt(scene, aspectRatio, angleY);

      if (!camera) {
        return;
      }

      const tool = viewer.tool as any;
      tool.lookAt(camera.origin, camera.target);
      // set zoommax distance to initial camera distance
      if (typeof tool.setMaxDistance === 'function') {
        tool.setMaxDistance(camera.distance);
      }
      lastFramedSceneVersionRef.current = sceneVersion;
    },
    [scene, sceneIsLoading, sceneVersion, styleType],
  );

  // Focus camera when a new scene is loaded into the viewer.
  useEffect(() => {
    frameScene();
  }, [frameScene]);

  const handleCameraReset = useCallback(() => {
    frameScene({ force: true });
  }, [frameScene]);

  const handleZoomIn = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    const tool = viewer.tool as any;
    if (typeof tool.zoomIn === 'function') {
      tool.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    const tool = viewer.tool as any;
    if (typeof tool.zoomOut === 'function') {
      tool.zoomOut();
    }
  }, []);

  return {
    config,
    featureFlags,
    handleDownloadImage,
    handleUpdate,
    handleCameraReset,
    handleZoomIn,
    handleZoomOut,
    sceneIsLoading,
    scene,
    stylePreviewUrl,
    styleNotFoundMessage,
    viewerRef,
    visible,
  };
};
